/* Iceberg-ui */
import { getTheme } from '@auviknetworks/iceberg-ui';

/* MUI */
import CssBaseline from '@mui/material/CssBaseline';
import {
    ThemeProvider as MUIThemeProvider,
    createTheme
} from '@mui/material/styles';
import { TypographyProps } from '@mui/system';

/* Styles */
import { AppStyles } from 'application/App/css/AppStyles';
import { BigCalendarStyles } from 'application/App/css/Libraries/BigCalendarStyles';
import { FileBrowserStyles } from 'application/App/css/Libraries/FileBrowserStyles';
import { IcebergMUIStyles } from 'application/App/css/Libraries/IcebergMUIStyles';
import { RechartStyles } from 'application/App/css/Libraries/RechartStyles';
import { ToasterStyles } from 'application/App/css/Libraries/ToasterStyles';
import { PrintStyles } from 'application/App/css/PrintStyles';

/* Hooks */
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import React, { ReactElement } from 'react';
// @ts-expect-error - Adding type definitions for styled-components creates a cascade of issues
import { ThemeProvider as IcebergThemeProvider } from 'styled-components';

import { defaultDarkTheme2023 } from './muiThemeDark';
import { defaultLightTheme2023 } from './muiThemeLight';

declare module '@mui/material/styles' {
    // Adding custom component type
    interface Components {
        Panel: {
            title: {
                large: Partial<TypographyProps>;
                medium: Partial<TypographyProps>;
                small: Partial<TypographyProps>;
            };
        };
    }
    interface TypeText {
        calendarSelectedSection: string;
        calendarSelectedSectionDisabled: string;
        noData: string;
        cardKPI: string;
        cardKPINoData: string;
        danger: string;
        disabled: string;
        emailAddress: string;
        error: string;
        link: { text: string; hover: string };
        overlappingAppsCount: string;
        pageNotFound: string;
        primary: string;
        primaryFaded: string;
        reportSummaryCardDark: string;
        reportSummaryCardLight: string;
        secondary: string;
        success: string;
        tag: { primary: string };
        tertiary: string;
        warning: string;
        white: string;
    }

    interface TypographyVariants {
        span: React.CSSProperties;
    }

    // allow configuration using `createTheme()`
    interface TypographyVariantsOptions {
        span?: React.CSSProperties;
    }

    interface ButtonPropsSizeOverrides {
        slim: true;
    }

    interface TypeBackground {
        avatar: string;
        calendarSelectedSection: string;
        configureIntegration: string;
        contractSectionTitle: string;
        contractsReport: string;
        contractSummaryCardWhite: string;
        contractTable: string;
        contractTableLight: string;
        danger: string;
        integrationMethodIcon: string;
        login: string;
        loginGraphic: string;
        optionIntegration: string;
        pageNotFound: string;
        primary: string;
        primaryLight: string;
        reports: string;
        reportSummaryCardDark: string;
        reportSummaryCardLight: string;
        saasOpsUserDetails: string;
        secondary: string;
        tertiary: string;
        timePeriodPicker: string;
        warning: string;
        white: string;
    }

    interface Palette {
        accountsReport: {
            chipType: {
                dormant: {
                    backgroundColor: string;
                    borderColor: string;
                    textColor: string;
                };
                active: {
                    backgroundColor: string;
                    borderColor: string;
                    textColor: string;
                };
            };
        };
        anmTrial: {
            backgroundColorPrimary: string;
            backgroundColorSecondary: string;
            errorScreen: {
                backgroundColor: string;
                borderColor: string;
                textColor: string;
            };
            linkColor: string;
            primaryTextColor: string;
            titleTextColor: string;
        };
        avatar: {
            appsAvatar: { backgroundColor: string };
            appsAvatarGroup: { backgroundColor: string };
            sso: { backgroundColor: string; color: string };
            ssoSecondary: {
                backgroundColor: string;
            };
            text: { white: string; black: string };
        };
        bigCalendar: {
            monthly: {
                background: string;
                border: string;
                icon: string;
            };
            renew: {
                background: string;
                border: string;
                icon: string;
            };
            agendaTableBorderColor: string;
            allDayCellBorderColor: string;
            appName: string;
            backgroundEvent: string;
            backgroundEventFocusOutline: string;
            backgroundEventSelectedBackground: string;
            buttonLink: string;
            buttonLinkHover: string;
            currentTimeIndicatorBackgroundColor: string;
            dayBgBorderColor: string;
            daySlotBackgroundEventBorderColor: string;
            headerBackgroundColor: string;
            headerColor: string;
            monthRowBorderColor: string;
            monthViewBorderColor: string;
            offRange: string;
            overflowingBorderColor: string;
            overlayBackgroundColor: string;
            overlayBorderColor: string;
            overlayHeaderBorderColor: string;
            timeContentBorderColor: string;
            timeHeaderBorderColor: string;
            timeHeaderGutterBackgroundColor: string;
            timeHeaderGutterBorderColor: string;
            timeSlotBorderColor: string;
            timeSlotGroupBorderColor: string;
            timeViewBorderColor: string;
            todayBackgroundColor: string;
            toolbarButtonActiveBackgroundColor: string;
            toolbarButtonActiveBorderColor: string;
            toolbarButtonActiveHoverBackgroundColor: string;
            toolbarButtonActiveHoverBorderColor: string;
            toolbarButtonActiveHoverColor: string;
            toolbarButtonBorderColor: string;
            toolbarButtonColor: string;
            toolbarButtonHoverBackgroundColor: string;
            toolbarButtonHoverBorderColor: string;
            toolbarButtonHoverColor: string;
            toolbarLabel: string;
        };
        border: {
            card: string;
            divider: string;
            darkerDivider: string;
            pageHeader: string;
            pageNotFound: string;
            primary: string;
            secondary: string;
            tertiary: string;
            highlightedCard: string;
            userDetailsAppHeadlineCard: string;
        };
        breach: {
            backgroundColor: {
                safe: string;
                critical: string;
            };
            borderColor: {
                safe: string;
                critical: string;
            };
            textColor: {
                default: string;
                safe: string;
                critical: string;
            };
        };
        button: {
            primary: string;
            cancel: string;
            danger: string;
            success: string;
            warning: string;
            white: string;
        };
        calendar: {
            monthly: string;
            renew: string;
        };
        chart: {
            axis: string;
            axisTickValue: string;
            axisLine: string;
            axisTickLine: string;
            bar: string;
            line: string;
            saasHealthLineGraph: string;
            tooltipBackground: string;
            tooltipObjectBackground: string;
            primary: string;
            secondary: string;
            tertiary: string;
            danger: string;
            success: string;
            warning: string;
        };
        checkbox: {
            border: {
                primary: string;
            };
            checked: {
                primary: string;
                secondary: string;
                tertiary: string;
            };
        };
        chip: {
            background: {
                primary: string;
                secondary: string;
                tertiary: string;
            };
            text: {
                primary: string;
                secondary: string;
            };
        };
        community: {
            likeBadge: {
                likedBackgroundColor: string;
                unlikedBackgroundColor: string;
                borderColor: string;
            };
        };
        confetti: {
            color1: string;
            color2: string;
            color3: string;
            color4: string;
            color5: string;
            color6: string;
            color7: string;
            color8: string;
        };
        contract: {
            chipType: {
                month: {
                    backgroundColor: string;
                    borderColor: string;
                    iconColor: string;
                };
                renew: {
                    backgroundColor: string;
                    borderColor: string;
                    iconColor: string;
                };
            };
        };
        customizedDot: {
            color: string;
            border: string;
        };
        danger: {
            main: string;
        };
        externalAppStatus: {
            critical: string;
            major: string;
            minor: string;
            none: string;
            resolved: string;
        };
        fileBrowser: {
            borderColor: string;
            linkColor: string;
            borderHighlight: string;
            itemColor: string;
            itemBackgroundColor: string;
            tableHover: string;
            tableLink: string;
            fileBrowser: string;
            fileBrowserSelected: string;
        };
        graph: {
            axisLabels: string;
            colors: string[];
        };
        hover: {
            primary: string;
            secondary: string;
            tertiary: string;
        };
        iceberg: {
            colorAction: string;
            colorBrandPrimary: string;
            colorBrandSecondary: string;
        };
        icebergForm: {
            formLabelColor: string;
            formLabelColorError: string;
            textFieldInputBackgroundColorReadOnly: string;
            textFieldInputBackgroundColor: string;
            textFieldInputColor: string;
            textFieldInputColorReadOnly: string;
            textFieldOutline: string;
            textFieldOutlineError: string;
            textFieldOutlineFocused: string;
            selectBackgroundColor: string;
            selectOutline: string;
            chevronIcon: string;
        };
        icon: {
            acceptSave: string;
            accordionExpand: string;
            accordionItem: string;
            avatar: string;
            badgeWarning: string;
            cancel: string;
            clientDefault: string;
            danger: string;
            disabled: string;
            discoveredByQuickScan: string;
            error: string;
            info: string;
            infoDark: string;
            managedByPartner: string;
            primary: string;
            search: string;
            success: string;
            tertiary: string;
            textColor: string;
            warning: string;
            white: string;
        };
        lifecycleStages: {
            background: {
                unapproved: string;
                notConfigured: string;
                evaluating: string;
                implementing: string;
                approved: string;
                decommissioning: string;
                decommissioned: string;
            };
            borderColor: {
                unapproved: string;
                notConfigured: string;
                evaluating: string;
                implementing: string;
                approved: string;
                decommissioning: string;
                decommissioned: string;
            };
            icon: {
                unapproved: string;
                notConfigured: string;
                evaluating: string;
                implementing: string;
                approved: string;
                decommissioning: string;
                decommissioned: string;
            };
            iconFilter: {
                unapproved: string;
                notConfigured: string;
                evaluating: string;
                implementing: string;
                approved: string;
                decommissioning: string;
                decommissioned: string;
            };
        };
        managedPicker: {
            background: {
                managed: string;
                unmanaged: string;
                unclassified: string;
            };
            borderColor: {
                managed: string;
                unmanaged: string;
                unclassified: string;
            };
            icon: {
                managed: string;
                unmanaged: string;
                unclassified: string;
            };
            iconFilter: {
                managed: string;
                unmanaged: string;
                unclassified: string;
            };
            textColor: {
                managed: string;
                unmanaged: string;
                unclassified: string;
            };
        };
        menuItem: {
            hoverBackgroundColor: string;
        };
        metadata: {
            background: string;
        };
        modal: {
            backgroundColor: {
                unapproved: string;
                notConfigured: string;
                evaluating: string;
                implementing: string;
                approved: string;
                decommissioning: string;
                decommissioned: string;
            };
            textColor: {
                unapproved: string;
                notConfigured: string;
                evaluating: string;
                implementing: string;
                approved: string;
                decommissioning: string;
                decommissioned: string;
            };
        };
        notes: {
            icon: {
                client: string;
                community: string;
                open: string;
                partner: string;
            };
        };
        onboarding: {
            backgroundColorPrimary: string;
            backgroundColorSecondary: string;
            backgroundColorGray: string;
            backgroundColorWhite: string;
            bonusConfigurationBadgeBackgroundColor: string;
            buttonPrimary: string;
            cardBodyText: string;
            checklistBackgroundColor: string;
            deployAgentInsteadGraphic: {
                backgroundColor: {
                    light: string;
                    medium: string;
                    dark: string;
                };
            };
            integrationCardBackgroundColor: string;
            integrationCardBorderColor: string;
            primaryTextColor: string;
            quickScanChooseCardBackgroundColor: string;
            tertiaryIconColor: string;
            tertiaryTextColor: string;
            titleTextColor: string;
        };
        pieChart: {
            personal: string;
            domain: string;
            na: string;
        };
        progressBar: {
            backgroundColor: string;
            textColor: {
                unapproved: string;
                notConfigured: string;
                evaluating: string;
                implementing: string;
                approved: string;
                decommissioning: string;
                decommissioned: string;
            };
        };
        providerGroup: {
            default: {
                borderColor: string;
                backgroundColor: string;
                text: string;
            };
            orgUnit: {
                borderColor: string;
                backgroundColor: string;
                text: string;
            };
        };
        qbrFirstReports: {
            AppClassificationScore: string;
            ManagedAppScore: string;
            ShadowITScore: string;
            UnapprovedAppScore: string;
            AppOverlapScore: string;
            SaaSSophisticationScore: string;
            scoreTextColor: {
                increase: string;
                decrease: string;
                unchanged: string;
            };
        };
        reports: {
            v2Header: {
                boxShadow: string;
            };
        };
        risk: {
            backgroundColor: {
                critical: string;
                high: string;
                medium: string;
                low: string;
                alert: string;
                safe: string;
                undefined: string;
            };
            borderColor: {
                critical: string;
                high: string;
                medium: string;
                low: string;
                alert: string;
                safe: string;
                undefined: string;
            };
            icon: {
                critical: string;
                high: string;
                medium: string;
                low: string;
                alert: string;
                safe: string;
                undefined: string;
            };
            iconFilter: {
                critical: string;
                high: string;
                medium: string;
                low: string;
                alert: string;
                safe: string;
                undefined: string;
            };
            pieChartColor: {
                critical: string;
                high: string;
                medium: string;
                low: string;
                alert: string;
                safe: string;
                undefined: string;
                default: string;
            };
        };
        saasScore: {
            borderColor: {
                excellent: string;
                medium: string;
                low: string;
                none: string;
            };
            fillColor: {
                excellent: string;
                medium: string;
                low: string;
                none: string;
            };
            unfilledColor: {
                excellent: string;
                medium: string;
                low: string;
                none: string;
            };
            text: {
                excellent: string;
            };
        };
        saasOpsAppList: {
            backgroundColor: {
                hover: {
                    current: string;
                    notCurrent: string;
                };
                current: string;
                notCurrent: string;
            };
            trash: {
                backgroundColor: string;
            };
            borderBottom: string;
        };
        saasOpsState: {
            background: {
                integrated: string;
            };
            borderColor: {
                integrated: string;
            };
            icon: {
                integrated: string;
            };
            textColor: {
                integrated: string;
            };
        };
        scripts: {
            color: string;
            backgroundColor: string;
            borderColor: string;
        };
        sidebar: {
            backgroundColor: string;
            backgroundColorBottomSection: string;
            clientTitle: string;
            clientName: string;
            divider: string;
            text: string;
            icon: string;
            itemSelected: {
                backgroundColor: string;
                hover: {
                    backgroundColor: string;
                };
            };
            item: {
                hover: {
                    backgroundColor: string;
                };
                bottomItemHover: {
                    backgroundColor: string;
                };
            };
            newBadge: string;
            mainTabSelected: {
                backgroundColor: string;
                hover: {
                    backgroundColor: string;
                };
            };
            mainTab: {
                hover: {
                    backgroundColor: string;
                };
                color: string;
            };
            subTabSelected: {
                borderLeftColor: string;
                hover: {
                    backgroundColor: string;
                };
            };
            subTab: {
                hover: {
                    backgroundColor: string;
                };
                color: string;
            };
        };
        sparkLines: {
            sparkLine: string;
            sparkBar: string;
            stroke: string;
        };
        ssoSupport: {
            globalSupport: {
                iconPrint: string;
                background: string;
            };
            tenantUse: {
                iconPrint: string;
                background: string;
            };
            tooltip: {
                divider: string;
            };
        };
        switchAuvik: {
            baseChecked: string;
            baseTrackBackground: string;
            baseFocusBorder: string;
            baseFocusColor: string;
            baseDisabled: string;
            thumbBackground: string;
            thumbColor: string;
            trackBackground: string;
        };
        tab: {
            divider: string;
            subdivider: string;
        };
        table: {
            border: string;
            dangerRowBackground: string;
            dangerRowText: string;
            evenRowBackground: string;
            emptyRowsBackground: string;
            footerBackground: string;
            headerRowBackground: string;
            headerBorderSortable: string;
            oddRowBackground: string;
            rowBackground: string;
            subtableBackground: string;
            subtableHeader: string;
            successRowBackground: string;
            successRowText: string;
            totalRowBackground: string;
        };
        tag: {
            background: string;
            borderColor: string;
            textColor: string;
        };
        tenantSelector: {
            input: {
                backgroundColor: string;
                borderColor: string;
            };
            menuListColor: string;
            paperProps: {
                item: {
                    color: string;
                    hover: {
                        backgroundColor: string;
                        color: string;
                    };
                };
                itemSelected: {
                    backgroundColor: string;
                    color: string;
                    hover: {
                        backgroundColor: string;
                    };
                };
                itemSelectedHover: {
                    backgroundColor: string;
                    color: string;
                };
                backgroundColor: string;
                borderColor: string;
            };
        };
        tertiary: { main: string };
        toastColors: {
            red: {
                background: string;
                border: string;
                icon: string;
                progressBar: string;
                text: string;
            };
            blue: {
                background: string;
                border: string;
                icon: string;
                progressBar: string;
                text: string;
            };
            green: {
                background: string;
                border: string;
                icon: string;
                progressBar: string;
                text: string;
            };
            yellow: {
                background: string;
                border: string;
                icon: string;
                progressBar: string;
                text: string;
            };
        };
        tooltips: {
            darkTooltip: {
                background: string;
                color: string;
                arrowColor: string;
            };
            lightTooltip: {
                background: string;
                border: string;
                color: string;
                arrowColor: string;
            };
        };
        topInfoBanner: {
            defaultBackgroundColor: string;
            defaultBorderColor: string;
            configureIntegration: {
                backgroundColor: string;
                borderColor: string;
                textColor: string;
                titleColor: string;
            };
        };
        trialExpiryJailCard: {
            backgroundTrialJailGradientEnd: string;
            backgroundTrialJailGradientStart: string;
            cardBackgroundColor: string;
            divider: string;
            icon: string;
            logoutIcon: string;
            logoutText: string;
            subcardBorderColor: string;
            subtitle: string;
            text: string;
            title: string;
        };
        usage: {
            barChart: {
                'High Usage': string;
                'Medium Usage': string;
                'Low Usage': string;
                Waste: string;
            };
            badgeIcon: {
                high: string;
                low: string;
                medium: string;
                waste: string;
            };
            badgeTextColor: {
                high: string;
                low: string;
                medium: string;
                waste: string;
            };
            icon: {
                high: string;
                low: string;
                medium: string;
                waste: string;
            };
            iconFilter: {
                high: string;
                low: string;
                medium: string;
                waste: string;
            };
            textColor: {
                high: string;
                low: string;
                medium: string;
                waste: string;
            };
        };
        userInvitationStatus: {
            accepted: {
                icon: string;
                background: string;
                borderColor: string;
                text: string;
            };
            pending: {
                icon: string;
                background: string;
                borderColor: string;
                text: string;
            };
        };
        viewPricing: {
            dividerPerUser: string;
            dividerExclusive: string;
            icon: string;
            textTitle: string;
            textSubtitle: string;
            textPerUser: string;
            textExclusive: string;
        };
        whiteBackground: string;
    }
}

// Add variant property to Badge
declare module '@mui/material/Badge' {
    interface BadgePropsVariantOverrides {
        newDot: true;
    }
}

// Update the Button's variant prop options
declare module '@mui/material/Button' {
    interface ButtonPropsSizeOverrides {
        slim: true;
        anmSmall: true;
    }
}

// Add variant property to IconButton
declare module '@mui/material/IconButton' {
    interface IconButtonOwnProps {
        variant?: 'slim';
    }
}

// Add variant property to TableRow
declare module '@mui/material/TableRow' {
    interface TableRowOwnProps {
        variant?: 'noHover';
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        span: true;
    }
}

const themes: { [key: string]: object } = {
    darkTheme2023: defaultDarkTheme2023,
    lightTheme2023: defaultLightTheme2023
};

export const getASMTheme = (userTheme: string) => {
    // Properties needed for iceberg to set the userTheme.
    let { colorAction } = defaultLightTheme2023.palette.iceberg;
    let { colorBrandPrimary }: { colorBrandPrimary: string } =
        defaultLightTheme2023.palette.iceberg;
    let { colorBrandSecondary }: { colorBrandSecondary: string } =
        defaultLightTheme2023.palette.iceberg;
    let themeName = 'lightTheme2023';

    if (userTheme === 'dark') {
        colorAction = defaultDarkTheme2023.palette.iceberg.colorAction;
        colorBrandPrimary =
            defaultDarkTheme2023.palette.iceberg.colorBrandPrimary;
        colorBrandSecondary =
            defaultDarkTheme2023.palette.iceberg.colorBrandSecondary;
        themeName = 'darkTheme2023';
    }

    // Needed for iceberg to set the userTheme.
    // Button main color, e.g., is managed by colorAction, and not by iceberg itself.
    // In the web-client, there are other settings that we won't be using here,
    // colorMapBackground, colorSidebarBackground, colorSidebarText, favicon, and logo.
    const themeSettingsObj: {
        colorAction: string;
        colorBrandPrimary: string;
        colorBrandSecondary: string;
        darkColorAction: string;
        darkColorBrandPrimary: string;
        darkColorBrandSecondary: string;
        themeName: string;
    } = {
        colorAction,
        colorBrandPrimary,
        colorBrandSecondary,
        darkColorAction: defaultDarkTheme2023.palette.iceberg.colorAction,
        darkColorBrandPrimary:
            defaultDarkTheme2023.palette.iceberg.colorBrandPrimary,
        darkColorBrandSecondary:
            defaultDarkTheme2023.palette.iceberg.colorBrandSecondary,
        themeName
    };

    const icebergTheme = getTheme(themeSettingsObj);

    const muiTheme = createTheme(themes[themeSettingsObj.themeName]);
    return {
        icebergTheme,
        muiTheme
    };
};

export const StylesAndColors = ({ children }: { children: ReactElement }) => {
    const userTheme = useUserTheme();
    const { icebergTheme, muiTheme: theme } = getASMTheme(userTheme);

    return (
        <IcebergThemeProvider theme={icebergTheme}>
            <MUIThemeProvider theme={theme}>
                <AppStyles theme={theme} />
                <BigCalendarStyles theme={theme} />
                <FileBrowserStyles theme={theme} />
                <ToasterStyles theme={theme} />
                <IcebergMUIStyles theme={theme} />
                <RechartStyles theme={theme} />
                <PrintStyles theme={theme} />
                <CssBaseline />
                {children}
            </MUIThemeProvider>
        </IcebergThemeProvider>
    );
};
